import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import {
  Camera,
  CameraCapability,
  CameraConfiguration,
  CameraConnectionType,
  CameraState,
} from 'types/camera';
import { RtspConfig } from 'types/rtsp_config';

import { useAPI } from 'hooks/api/useAPI';
import {
  updateQueriesData,
  UpdateQueriesDataContext,
} from 'services/update_queries_data';

type UseUpdateCameraParams = {
  id: string;
  name?: string;
  status?: CameraState;
  model?: string | null;
  conn_type?: CameraConnectionType;
  uri?: string | null;
  gateway_id?: string | null;
  ip_local?: string | null;
  ip_ext?: string | null;
  mac_address?: string | null;
  username?: string | null;
  password?: string | null;
  configuration?: CameraConfiguration | null;
  capabilities?: CameraCapability[] | null;
  snapshot_file_id?: string | null;
  external_id?: string | null;
  rtsp_config?: RtspConfig | null;
};

export function useUpdateCamera({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<
  Camera,
  APIError,
  UseUpdateCameraParams,
  UpdateQueriesDataContext<Camera>
> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ id, ...params }) => {
      const { data } = await client.put(
        `/apps/${applicationID}/cameras/${id}`,
        {
          ...params,
        }
      );

      return data;
    },
    {
      ...options,
      onMutate: async ({ id, ...params }) => {
        await queryClient.cancelQueries(['cameras']);
        await queryClient.cancelQueries(['camera']);

        onMutate?.({ id, ...params });

        return updateQueriesData({
          queryClient,
          listQueryKey: ['cameras'],
          singleQueryKey: ['camera'],
          ids: [id],
          updateData: (camera) => ({ ...camera, ...params }),
        });
      },
      onError: (error, camera, context) => {
        onError?.(error, camera, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled: (...args) => {
        onSettled?.(...args);

        queryClient.invalidateQueries(['cameras']);
        queryClient.invalidateQueries(['camera']);
      },
    }
  );
}
